import { type BenchmarkPreviewView, type ImageType, toBenchmarkPreviewUrls } from '@vizit/api';
import { BENCHMARK_MIN_IMAGES, UNIVERSAL_BENCHMARK_KEY, UNIVERSAL_BENCHMARK_NAME } from '@vizit/constants';
import type { BenchmarkMosaicUrl } from '@vizit/molecules';
import { plural } from '@vizit/utils';

const VIZIT_RETAILERS = ['amazon', 'walmart', UNIVERSAL_BENCHMARK_KEY] as const;

/** A retailer with a Vizit benchmark; the pickers offer Universal (`us_composite`) only behind its flag. */
type VizitRetailer = (typeof VIZIT_RETAILERS)[number];

function isVizitRetailer(retailer: string): retailer is VizitRetailer {
  return (VIZIT_RETAILERS as readonly string[]).includes(retailer);
}

/** Each Vizit benchmark's name, as the pickers and the places that name a scored pair write it. */
const VIZIT_BENCHMARK_NAMES: Record<VizitRetailer, string> = {
  amazon: 'Amazon',
  walmart: 'Walmart',
  [UNIVERSAL_BENCHMARK_KEY]: `${UNIVERSAL_BENCHMARK_NAME} (Amazon + Walmart)`,
};

/**
 * One row of a benchmark picker. Vizit benchmarks have no id and are identified
 * by `retailer`; custom benchmarks have an id and a locked scope.
 */
interface BenchmarkOption {
  kind: 'vizit' | 'custom';
  /** Set for Vizit benchmarks, which the API addresses by retailer rather than id. */
  retailer?: VizitRetailer;
  /** Set for custom benchmarks. */
  id?: string;
  name: string;
  /** `null` for Vizit benchmarks, which score every image type. */
  scope: ImageType | null;
  /** `null` for Vizit benchmarks, whose pool depends on the product category. */
  imageCount: number | null;
  /** `null` for Vizit benchmarks. */
  minimumImages: number | null;
  /** True when the benchmark cannot score yet, e.g. a custom set under its minimum. */
  disabled: boolean;
  /** Why a disabled Vizit benchmark cannot be picked, shown under its name; a custom set shows its image count. */
  disabledReason?: string;
  /** Secondary text for surfaces with room for it; `null` or absent when the benchmark has none. */
  description?: string | null;
  /** Images the set should have for stable scores; `null` for Vizit benchmarks. */
  recommendedImages?: number | null;
  /**
   * Up to four mosaic thumbnails, each the 64px resize falling back to the original (see `toBenchmarkPreviewUrls`);
   * absent or short when the source route does not carry them, which leaves empty cells.
   */
  previewUrls?: BenchmarkMosaicUrl[];
}

/** The Vizit benchmarks a new collection can default to; Universal is left out. */
const VIZIT_BENCHMARK_OPTIONS: BenchmarkOption[] = [
  {
    kind: 'vizit',
    retailer: 'amazon',
    name: VIZIT_BENCHMARK_NAMES.amazon,
    scope: null,
    imageCount: null,
    minimumImages: null,
    disabled: false,
  },
  {
    kind: 'vizit',
    retailer: 'walmart',
    name: VIZIT_BENCHMARK_NAMES.walmart,
    scope: null,
    imageCount: null,
    minimumImages: null,
    disabled: false,
  },
];

/** Stable key for a select item: `vizit:amazon` or `custom:<id>`. */
function getBenchmarkOptionKey(option: BenchmarkOption): string {
  return option.kind === 'vizit' ? `vizit:${option.retailer}` : `custom:${option.id}`;
}

const DEFAULT_BENCHMARK_OPTION_KEY = getBenchmarkOptionKey(VIZIT_BENCHMARK_OPTIONS[0]);

/** A select item key read from a URL: `vizit:` with a known retailer or `custom:` with an id, else `undefined`. */
function parseBenchmarkOptionKey(value: unknown): string | undefined {
  if (typeof value !== 'string') {
    return undefined;
  }
  const [kind, id] = value.split(/:(.*)/s);
  const isKey = kind === 'vizit' ? !!id && isVizitRetailer(id) : kind === 'custom' && !!id;
  return isKey ? value : undefined;
}

interface CustomBenchmarkLike {
  id: string;
  name: string | null;
  scope: string;
  image_count: number;
  minimum_images?: number;
  description?: string | null;
  recommended_images?: number;
  previews?: BenchmarkPreviewView[];
}

const IMAGE_TYPES: readonly string[] = ['hero', 'carousel'] satisfies ImageType[];

/**
 * Maps an API benchmark row to a picker option, disabling it while it is under its
 * minimum. Returns `null` for a scope this picker does not know, so it is left out
 * rather than mislabelled.
 */
function toCustomBenchmarkOption(benchmark: CustomBenchmarkLike): BenchmarkOption | null {
  if (!IMAGE_TYPES.includes(benchmark.scope)) return null;

  const minimumImages = benchmark.minimum_images ?? BENCHMARK_MIN_IMAGES;

  return {
    kind: 'custom',
    id: benchmark.id,
    name: benchmark.name ?? 'Untitled benchmark',
    scope: benchmark.scope as ImageType,
    imageCount: benchmark.image_count,
    minimumImages,
    disabled: benchmark.image_count < minimumImages,
    description: benchmark.description ?? null,
    recommendedImages: benchmark.recommended_images ?? null,
    previewUrls: toBenchmarkPreviewUrls(benchmark.previews ?? []),
  };
}

/** Maps a list of API benchmark rows, skipping the ones with an unknown scope. */
function toCustomBenchmarkOptions(benchmarks: CustomBenchmarkLike[]): BenchmarkOption[] {
  return benchmarks.map(toCustomBenchmarkOption).filter((option): option is BenchmarkOption => option !== null);
}

const SCOPE_LABELS: Record<ImageType, string> = { hero: 'HERO', carousel: 'CAROUSEL' };

/** The image type as a badge or field reads it: "Hero", "Carousel". */
const IMAGE_TYPE_NAMES: Record<ImageType, string> = { hero: 'Hero', carousel: 'Carousel' };

/** How many images a custom set still needs before it can score, 0 once it has enough (and for Vizit ones). */
function getBenchmarkImagesNeeded(option: BenchmarkOption): number {
  return Math.max((option.minimumImages ?? 0) - (option.imageCount ?? 0), 0);
}

/** Reads "16 more images needed", the reason an under-minimum set cannot be picked. */
function imagesNeededLabel(option: BenchmarkOption): string {
  return `${plural(getBenchmarkImagesNeeded(option), 'more image')} needed`;
}

/** The heading of the custom group, naming the image type when the picker is scoped to one. */
function getCustomBenchmarksHeader(scope: ImageType | undefined): string {
  return scope ? `CUSTOM ${SCOPE_LABELS[scope]} BENCHMARKS` : 'CUSTOM BENCHMARKS';
}

function findBenchmarkOption(options: BenchmarkOption[], key: string | undefined) {
  return options.find((option) => getBenchmarkOptionKey(option) === key);
}

export {
  DEFAULT_BENCHMARK_OPTION_KEY,
  IMAGE_TYPE_NAMES,
  VIZIT_BENCHMARK_NAMES,
  VIZIT_BENCHMARK_OPTIONS,
  findBenchmarkOption,
  getBenchmarkImagesNeeded,
  getBenchmarkOptionKey,
  getCustomBenchmarksHeader,
  imagesNeededLabel,
  isVizitRetailer,
  parseBenchmarkOptionKey,
  toCustomBenchmarkOption,
  toCustomBenchmarkOptions,
};
export type { BenchmarkOption, VizitRetailer };
